import { Button } from "@/components/ui/button";
import {
  MicrophonePermissionResult,
  useMicrophonePermission,
} from "@/utils/microphonePermissions";
import { Mic, Settings } from "lucide-react";
import React, { useState } from "react";

interface MicrophonePermissionRequestProps {
  onPermissionGranted?: () => void;
  onPermissionDenied?: (result: MicrophonePermissionResult) => void;
}

export const MicrophonePermissionRequest: React.FC<
  MicrophonePermissionRequestProps
> = ({ onPermissionGranted, onPermissionDenied }) => {
  const {
    permissionState,
    isLoading,
    requestPermission,
    checkPermission,
    isGranted,
    isDenied,
    canRequest,
  } = useMicrophonePermission();

  const [isRequesting, setIsRequesting] = useState(false);

  const handleRequestPermission = async () => {
    setIsRequesting(true);
    try {
      const result = await requestPermission();

      if (result.state === "granted") {
        onPermissionGranted?.();
      } else {
        onPermissionDenied?.(result);
      }
    } finally {
      setIsRequesting(false);
    }
  };

  const handleRetryCheck = async () => {
    await checkPermission();
  };

  // If permission is already granted, don't show this component
  if (isGranted) {
    return null;
  }

  const getDescription = () => {
    if (permissionState?.state === "denied") {
      return "Microphone access is blocked. Please enable it in your browser settings to use audio features.";
    } else if (permissionState?.state === "unsupported") {
      return "Your browser doesn't support microphone access. Please use a modern browser to enable audio features.";
    } else {
      return "We need access to your microphone to configure audio settings and enable voice recording features.";
    }
  };

  const getButtonText = () => {
    if (isDenied) {
      return "Check Again";
    } else if (isRequesting) {
      return "Requesting Access...";
    } else {
      return "Enable Microphone";
    }
  };

  const getButtonIcon = () => {
    if (isDenied) {
      return <Settings className="h-4 w-4" />;
    } else {
      return <Mic className="h-4 w-4" />;
    }
  };

  const handleButtonClick = () => {
    if (isDenied) {
      handleRetryCheck();
    } else if (canRequest) {
      handleRequestPermission();
    }
  };

  return (
    <div className="flex flex-col gap-6 @md:flex-row @md:items-center @md:justify-between">
      <div className="space-y-1">
        <h3 className="font-medium">Microphone Access</h3>
        <p className="text-sm text-muted-foreground">{getDescription()}</p>
      </div>
      <Button
        variant="outline"
        onClick={handleButtonClick}
        disabled={isLoading || isRequesting || (!canRequest && !isDenied)}
      >
        {getButtonIcon()}
        {getButtonText()}
      </Button>
    </div>
  );
};

export default MicrophonePermissionRequest;
